import { CircularLine, CircularTerm, FinanceCircular } from '../types/catalog';

// Volkswagen Financial Services — Circular Nº 62-26 (Buenos Aires, 6 de octubre de 2026). Campañas octubre 2026.
// Cuota con el coeficiente "Cuota inicial (*)" cada $1.000.
// "Quebranto" se guarda como dato interno (no se muestra al público).

const t = (term: number, tna: number, dealerContributionPct: number, installmentPer1000: number): CircularTerm => ({
  term,
  tna,
  dealerContributionPct,
  installmentPer1000,
});

/** Autos de pasajeros VW (excluye Amarok y utilitarios). */
const AUTOS = ['Polo', 'Virtus', 'Nivus', 'T-Cross', 'Tera', 'Taos', 'Tiguan'];
const TERA_NIVUS_TCROSS = ['Tera', 'Nivus', 'T-Cross'];

const line = (id: string, name: string, detail: string, models: string[], maxAmount: number, terms: CircularTerm[]): CircularLine => ({
  id: `vw-${id}`,
  name,
  detail,
  maxAmount,
  models,
  rateType: 'fija',
  audience: 'general',
  channel: 'VWFS',
  terms,
});

export const volkswagenCircular: FinanceCircular = {
  brandId: 'volkswagen',
  lender: 'Volkswagen Financial Services',
  reference: 'Circular Nº 62-26 · Campañas octubre 2026',
  validFrom: '2026-10-06',
  validUntil: '2026-10-31',
  managementFeePct: null,
  notes: ['La cuota no incluye seguro automotor.', 'Sujeto a aprobación crediticia de Volkswagen Financial Services.'],
  lines: [
    // ---------- Tera, Nivus y T-Cross ----------
    line('tnt-t0-23', 'Tasa 0%', 'Tera, Nivus y T-Cross', TERA_NIVUS_TCROSS, 23000000, [t(12, 0, 5, 83.33), t(18, 0, 11.5, 55.56)]),
    line('tnt-t0-17', 'Tasa 0%', 'Tera, Nivus y T-Cross', TERA_NIVUS_TCROSS, 17500000, [t(24, 0, 12.5, 41.67)]),
    line('tnt-t0-12', 'Tasa 0%', 'Tera, Nivus y T-Cross', TERA_NIVUS_TCROSS, 12000000, [t(36, 0, 12, 27.78)]),
    line('tnt-sin-quebranto', 'Tasa 0% sin quebranto', 'Tera, Nivus y T-Cross', TERA_NIVUS_TCROSS, 18000000, [t(12, 0, 0, 83.33)]),

    // ---------- Autos ----------
    line('autos-fm-28', 'Financiá más', 'Autos', AUTOS, 28000000, [t(12, 0, 9, 83.33)]),
    line('autos-fm-20', 'Financiá más', 'Autos', AUTOS, 20000000, [t(18, 0, 11, 55.56)]),
    line('autos-fm-17', 'Financiá más', 'Autos', AUTOS, 17000000, [t(24, 0, 12.5, 41.67)]),
    line('autos-especial-23', 'Especial Autos', 'Autos', AUTOS, 23000000, [t(12, 0, 10.5, 83.33)]),
    line('autos-especial-15', 'Especial Autos', 'Autos', AUTOS, 15500000, [t(18, 0, 12.25, 55.56)]),
    line('autos-especial-17', 'Especial Autos', 'Autos', AUTOS, 17000000, [t(24, 11.5, 12, 48.85)]),
    line('autos-sq-15', 'Sin quebranto', 'Autos', AUTOS, 15000000, [t(12, 0, 0, 83.33)]),
    line('autos-sq-26', 'Sin quebranto', 'Autos', AUTOS, 26000000, [t(12, 19.9, 0, 96.07)]),
    line('autos-lp-20', 'Largo plazo 20MM', 'Autos', AUTOS, 20000000, [t(36, 18, 10, 39.3), t(48, 23, 10, 36.08), t(60, 26, 10, 34.49)]),
    line('autos-lp-24', 'Largo plazo 24MM', 'Autos', AUTOS, 24000000, [t(36, 21.5, 10, 41.69), t(48, 25.9, 10, 38.19), t(60, 28.5, 10, 36.43)]),
    line('polo-track-robust-lp', 'Largo plazo', 'Polo Track / Robust', ['Polo Track', 'Robust'], 18500000, [t(36, 18, 10, 39.3), t(48, 23, 10, 36.08), t(60, 26, 10, 34.49)]),

    // ---------- Amarok ----------
    line('amarok-fm-36', 'Financiá más', 'Amarok', ['Amarok'], 36500000, [t(12, 0, 11.5, 83.33)]),
    line('amarok-fm-24', 'Financiá más', 'Amarok', ['Amarok'], 24500000, [t(18, 0, 12, 55.56)]),
    line('amarok-fm-16', 'Financiá más', 'Amarok', ['Amarok'], 16500000, [t(24, 0, 11, 41.67)]),
    line('amarok-sq-18', 'Sin quebranto', 'Amarok', ['Amarok'], 18500000, [t(12, 0, 0, 83.33)]),
    line('amarok-sq-28', 'Sin quebranto', 'Amarok', ['Amarok'], 28000000, [t(12, 19.9, 0, 96.07)]),
    line('amarok-lp-40', 'Largo plazo 40MM', 'Amarok', ['Amarok'], 40000000, [t(36, 26.9, 10, 45.48), t(48, 30.5, 10, 41.63), t(60, 32.5, 10, 39.59)]),
    line('amarok-trendline-26', 'Amarok Trendline', 'Exclusiva Amarok Trendline', ['Amarok Trendline'], 26500000, [t(12, 0, 10, 83.33)]),
    line('amarok-trendline-20', 'Amarok Trendline', 'Exclusiva Amarok Trendline', ['Amarok Trendline'], 20000000, [t(24, 11.9, 11, 49.11)]),
  ],
};
